import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { LindList } from './KindList';
import * as S from './AdCardNew.styled';
import { AdStatus } from '@/types';
import { Ad } from '@/api/types/common.types';
import { formatNumber, getAdLabelKey } from '@/utils/helpers';
import { useIsShop } from '@/hooks/useIsShop';
import { getMessageTime } from '@/utils/datetime.utils';
import {
  HeartActiveIcon,
  HeartIconInactive,
} from '@/components/UI/Icons/Icons';
import { useBookmark } from '@/hooks/useBookmark';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ROUTES } from '@/constants/routes';
import NoSsr from '@/components/NoSsr/NoSsr';
import { useResponsive } from '@/hooks/useResponsive';
import { AdTransitionSource } from '@/api/types/ads.types';
import { getAdTransitionSourceCookie } from '@/utils/cookies';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { HighlightedTitle } from '@/features/PhoneNumberSearch';

interface AdCardNewProps {
  ad?: Ad;
  priority?: boolean;
  hasBookmarkButton?: boolean;
  onRemoveBookmark?: (ad: Ad) => void;
  onAddBookmark?: (ad: Ad) => void;
  adTransitionSource?: AdTransitionSource;
  canShowViewedState?: boolean;
  viewedFromStorage?: boolean;
  selection?: {
    isActive: boolean;
    isSelected: boolean;
    onToggle: () => void;
  };
  hasStatusOverlay?: boolean;
  phoneNumberFilter?: string;
}

const AdCardNewInner = ({
  ad,
  onRemoveBookmark,
  onAddBookmark,
  priority = false,
  hasBookmarkButton = true,
  adTransitionSource,
  canShowViewedState,
  viewedFromStorage,
  selection,
  hasStatusOverlay = true,
  phoneNumberFilter,
}: AdCardNewProps) => {
  const isShop = useIsShop(undefined, { cacheOnly: true });
  const { pathname } = useRouter();
  const { t, i18n } = useTranslation();
  const { refetch } = useUserFavorites({ skip: true });
  const { isBookmarked, toggleBookmark } = useBookmark({ ad });
  const { isDesktop } = useResponsive();

  const isBookmarksPage = pathname === ROUTES.bookmarks;
  const isShopAdBookmark = !isBookmarked && isShop && ad?.shop;
  const isSelectionActive = selection?.isActive ?? false;
  const isBookmarkButtonVisible = hasBookmarkButton && !isSelectionActive;
  const cardLegacyId = ad?.legacyResourceId
    ? String(ad.legacyResourceId)
    : undefined;
  const isViewedStateVisible = canShowViewedState && viewedFromStorage;

  const statusText = useMemo(() => {
    switch (ad?.status) {
      case AdStatus.Expired:
        return t('pages.ad.ad_inactive');
      case AdStatus.Archived:
        return t('pages.ad.ad_archived');
      default:
        return '';
    }
  }, [ad?.status]);

  const hasStatus = hasStatusOverlay && statusText.length > 0;

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    await toggleBookmark();
    await refetch();

    if (ad.isBookmarked) {
      onRemoveBookmark?.(ad);
    } else {
      onAddBookmark?.(ad);
    }
  };

  const toggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    selection?.onToggle();
  };

  const trackAdTransitionSourceCookie = () => {
    if (adTransitionSource && cardLegacyId) {
      getAdTransitionSourceCookie(cardLegacyId).set(adTransitionSource);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isSelectionActive) {
      toggleSelection(e);
      return;
    }

    trackAdTransitionSourceCookie();
  };

  const handleCardAuxClick = (e: React.MouseEvent) => {
    if (e.button !== 1) return;

    trackAdTransitionSourceCookie();
  };

  const handleCardTouchStart = () => {
    if (isSelectionActive) return;

    trackAdTransitionSourceCookie();
  };

  if (!ad) {
    return (
      <S.CardPlaceholder>
        <S.ImageWrapper>
          <SkeletonLoader height="100%" />
        </S.ImageWrapper>
        <SkeletonLoader height={15} width={130} />
        <SkeletonLoader height={15} width={120} />
        <SkeletonLoader height={15} width={100} />
        <SkeletonLoader height={15} width={100} />
      </S.CardPlaceholder>
    );
  }

  const labelKey = getAdLabelKey(ad.features);
  const labelText = labelKey ? t(labelKey) : null;

  return (
    <S.Card
      href={ad?.path ?? ''}
      onClick={handleCardClick}
      onAuxClick={handleCardAuxClick}
      onTouchStart={handleCardTouchStart}
      onContextMenu={trackAdTransitionSourceCookie}
      dataTestId={CommonTestIds.adCards.card}
      data-stat={!isBookmarksPage ? 'ad-card-link' : undefined}
    >
      <S.ImageWrapper>
        {canShowViewedState && (
          <NoSsr>
            {isViewedStateVisible && (
              <>
                <S.ViewedOverlay
                  data-testid={CommonTestIds.adCards.viewedOverlay}
                />
                <S.ViewedBadge data-testid={CommonTestIds.adCards.viewed}>
                  {t('common.viewed')}
                </S.ViewedBadge>
              </>
            )}
          </NoSsr>
        )}

        {hasStatus ? (
          <S.StatusLabel data-testid={CommonTestIds.adCards.photoOverlay}>
            <Typography.Body>{statusText}</Typography.Body>
          </S.StatusLabel>
        ) : null}

        {isBookmarkButtonVisible && (
          <S.BookmarkButton
            onClick={toggleFavorite}
            onTouchStart={e => e.stopPropagation()}
            className="products-bookmarking"
            data-stat={isShopAdBookmark ? 'shop-ad-bookmarking' : undefined}
            data-testid={CommonTestIds.adCards.bookmarkButton}
          >
            {isBookmarked ? (
              <HeartActiveIcon
                stroke="none"
                data-testid={CommonTestIds.adCards.isBookmarked}
              />
            ) : (
              <HeartIconInactive
                data-testid={CommonTestIds.adCards.isNotBookmarked}
                className="add_bookmark bookmark__link"
              />
            )}
          </S.BookmarkButton>
        )}

        {isSelectionActive && (
          <S.Checkbox
            data-testid={CommonTestIds.adCards.selectionCheckbox}
            onClick={toggleSelection}
            onTouchStart={e => e.stopPropagation()}
            $isChecked={selection?.isSelected ?? false}
          />
        )}

        <S.Image
          alt={ad.title}
          title={ad.title}
          src={
            isDesktop && ad.desktopPhoto ? ad.desktopPhoto.url : ad.photo.url
          }
          {...(priority ? { fetchpriority: 'high' } : { loading: 'lazy' })}
        />

        {labelText ? <S.Label>{labelText}</S.Label> : null}
      </S.ImageWrapper>

      <S.Content>
        {ad.price !== null && (
          <S.Price data-testid={CommonTestIds.adCards.price}>
            {formatNumber(ad.price, 2, true) || 0} ₼
          </S.Price>
        )}
        <S.Title
          $hasPadding={ad.price === null}
          data-testid={CommonTestIds.adCards.title}
        >
          <HighlightedTitle title={ad.title} filter={phoneNumberFilter} />
        </S.Title>
        <S.Footer>
          <S.DateAndRegion data-testid={CommonTestIds.adCards.additional}>
            {`${ad.region}, `}
            <NoSsr
              fallback={<SkeletonLoader as="span" width={90} height={15} />}
            >
              {getMessageTime(new Date(ad.updatedAt), i18n, true)}
            </NoSsr>
          </S.DateAndRegion>
          <LindList kinds={ad.kinds} />
        </S.Footer>
      </S.Content>
    </S.Card>
  );
};

export const AdCardNew = React.memo(AdCardNewInner);
